import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { DropdownMenuItem } from "~/components/dropdown-menu/item";
import { Permission } from "~/models/dto";

import { Api, type Monitor } from "../common/openapi";
import { usePermissions } from "../common/use-permissions";
import { ErrorMessage } from "../components/_notification-error";
import { LinkTo } from "../components/react/link";
import { yesNoModalConfirm } from "../components/react/modal/modal-utils";
import { DONT_AUTO_DISMISS, notification } from "../components/react/notification/Notification";
import { isHTTPError } from "../generic/request";
import {
	useDeleteMonitor,
	useInvalidateMonitorOverviewQuery,
	useInvalidateMonitorStatusQuery,
	useUpdateMonitorStatus,
} from "../queries";
import { MONITORS_PATH } from "../routes/Routes";

interface MonitorContextProps {
	monitor: Monitor;
}

export const MonitorContextMenu: React.FC<MonitorContextProps> = ({ monitor }) => {
	const permissions = usePermissions();
	const hasCreateMonitorsPermission = permissions?.hasSystemPermission(Permission.CreateMonitors);
	const hasUpdateMonitorsPermission = permissions?.hasSystemPermission(Permission.UpdateMonitors);
	const hasDeleteMonitorsPermission = permissions?.hasSystemPermission(Permission.DeleteMonitors);

	const isEditDisabled = !(monitor.canEdit || monitor.canClone);
	const isDeleteDisabled = !monitor.canEdit || monitor.sourceDetails != null;

	const invalidateMonitorOverviewList = useInvalidateMonitorOverviewQuery();
	const invalidateMonitorStatus = useInvalidateMonitorStatusQuery();

	const { isLoading: isDeleting, mutate: deleteMonitor } = useDeleteMonitor({
		onSuccess: () => {
			notification.success({
				message: `Successfully deleted monitor "${monitor.name}"`,
			});

			return invalidateMonitorOverviewList();
		},

		onError: (error) => {
			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: (
					<ErrorMessage error={isHTTPError(error) ? error : undefined}>
						Failed to delete monitor "{monitor.name}"
					</ErrorMessage>
				),
			});
		},
	});

	const { isLoading: isUpdating, mutate: updateMonitorStatus } = useUpdateMonitorStatus({
		onSuccess: (result) => {
			notification.success({
				message: `Monitor ${result.name} ${result.status === Api.MonitorStatusValue.ENABLED ? "enabled" : "disabled"}.`,
			});

			return Promise.all([invalidateMonitorOverviewList(), invalidateMonitorStatus()]);
		},
		onError: (error) => {
			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: <ErrorMessage error={error}>{`Can't change status for monitor ${monitor.id}.`}</ErrorMessage>,
			});
		},
	});

	const onDelete = async () => {
		const isConfirmed = await yesNoModalConfirm(`Are you sure you want to delete the "${monitor.name}" monitor?`);

		if (isConfirmed) {
			deleteMonitor(monitor.id);
		}
	};

	return (
		<>
			{hasCreateMonitorsPermission && hasUpdateMonitorsPermission && (
				<TooltipBoundary disableHoverableContent hide={!isEditDisabled}>
					<TooltipTrigger asChild>
						<DropdownMenuItem asChild disabled={isEditDisabled || isUpdating || isDeleting}>
							<LinkTo
								disabled={isEditDisabled || isUpdating || isDeleting}
								params={{ id: monitor.id }}
								search={["timeRange", "timestamp", "detachedFilters"]}
								theme="unstyled"
								to={MONITORS_PATH}
							>
								Edit
							</LinkTo>
						</DropdownMenuItem>
					</TooltipTrigger>

					<Tooltip className="tooltip/tooltip">This is a system monitor which can not be edited at the moment.</Tooltip>
				</TooltipBoundary>
			)}

			{hasUpdateMonitorsPermission && monitor.status === Api.MonitorStatusValue.ENABLED && (
				<DropdownMenuItem
					asChild={false}
					disabled={isUpdating || isDeleting}
					onSelect={() => updateMonitorStatus({ monitorId: monitor.id, status: Api.MonitorStatusValue.DISABLED })}
				>
					Disable
				</DropdownMenuItem>
			)}

			{hasUpdateMonitorsPermission && monitor.status === Api.MonitorStatusValue.DISABLED && (
				<DropdownMenuItem
					asChild={false}
					disabled={isUpdating || isDeleting}
					onSelect={() => updateMonitorStatus({ monitorId: monitor.id, status: Api.MonitorStatusValue.ENABLED })}
				>
					Enable
				</DropdownMenuItem>
			)}

			{hasDeleteMonitorsPermission && (
				<TooltipBoundary disableHoverableContent hide={!isDeleteDisabled}>
					<TooltipTrigger asChild>
						<DropdownMenuItem
							asChild={false}
							disabled={isDeleteDisabled || isUpdating || isDeleting}
							onSelect={onDelete}
						>
							Delete
						</DropdownMenuItem>
					</TooltipTrigger>

					<Tooltip className="tooltip/tooltip">This is a system monitor which can not be deleted.</Tooltip>
				</TooltipBoundary>
			)}
		</>
	);
};
